/** Body the note carries for a topic the guide presented and the user left blank. */
export const NOTE_NOT_ADDRESSED_BODY = 'Not addressed this visit';

/**
 * Heading for prose the model wrote under no header this frontend recognizes. Keeping it under a
 * heading of its own is what stops a section the model invented from reading like a section of the
 * form, while still leaving nothing the model produced on the floor.
 */
export const NOTE_UNSTRUCTURED_HEADING = 'Additional Narrative';

/**
 * Section headers the PREVIOUS note prompt emitted, in the order it emitted them.
 *
 * The note's sections are now the guide's own sections, so this list is not the note's structure
 * any more — it is a back-compat vocabulary. During a deploy window where this frontend runs
 * against a welby-micro-service still on the old prompt, the model answers with these headers;
 * recognizing them (see `legacyNoteSection`) is what lets that prose still land in the matching
 * guide section instead of being dumped at the end of the note.
 */
export const LEGACY_NOTE_SECTIONS: readonly string[] = [
  'Contact Type',
  'Call Opening & Identity Verification',
  'Reason for Contact',
  'Vitals Reported by Patient',
  'Symptoms Reported',
  'Medication Review',
  'RPM Device Review',
  'Care Gaps Addressed',
  'Patient Education',
  'Safety Review',
  'Escalation/Follow-up',
  'Next Contact',
  'Additional Notes',
];

/**
 * Guide section -> legacy header rules, most specific first. Matched against
 * "<sectionId> <sectionLabel>" lowercased, so a rule fires on either the authored id or the
 * clinical label: guides name the same topic differently across diagnoses (`bp_review` /
 * "Blood Pressure Review", `blood_sugar_review`, `breathing_symptom_review`, `weight_check`,
 * `self_reported_vitals` all report vitals) and new guides are seeded in Firestore without a
 * frontend release.
 *
 * Order is load-bearing: the vitals rule precedes the symptoms one so COPD's
 * "Breathing & Symptom Review" is recognized as a vitals review rather than a symptom screen.
 *
 * A rule must not be broad enough to make TWO sections of the SAME guide resolve to one legacy
 * header: the first in form order claims the key and the second is left with none. That is why the
 * education rule matches "education" only and no longer matches "goal" / "self-management" —
 * general_ma has both `health_goals` ("Health Goals & Self-Management", form order 4) and
 * `education` ("Reinforced Education Per Care Plan", order 7), so the broader rule let health_goals
 * claim "Patient Education" and the old prompt's education prose was filed as goal-setting.
 * `health_goals` deliberately resolves to no legacy header instead: goals are not one of the 13
 * sections the old prompt emitted, and a header that maps to nothing now lands under
 * `NOTE_UNSTRUCTURED_HEADING` rather than being appended to whatever section preceded it.
 */
const LEGACY_SECTION_RULES: { pattern: RegExp; section: string }[] = [
  { pattern: /identity|opening/, section: 'Call Opening & Identity Verification' },
  { pattern: /escalation/, section: 'Escalation/Follow-up' },
  { pattern: /medication|adherence|\bmeds\b/, section: 'Medication Review' },
  { pattern: /\brpm\b|device/, section: 'RPM Device Review' },
  { pattern: /education/, section: 'Patient Education' },
  { pattern: /safety/, section: 'Safety Review' },
  { pattern: /care.?gap/, section: 'Care Gaps Addressed' },
  { pattern: /next.?(contact|check)/, section: 'Next Contact' },
  {
    pattern: /vital|blood.?pressure|\bbp\b|blood.?sugar|glucose|a1c|weight|breathing|oxygen|spo2|reading/,
    section: 'Vitals Reported by Patient',
  },
  { pattern: /symptom/, section: 'Symptoms Reported' },
  { pattern: /contact.?type|call.?type/, section: 'Contact Type' },
  { pattern: /summary|additional/, section: 'Additional Notes' },
  { pattern: /check.?in|reason/, section: 'Reason for Contact' },
];

/**
 * The legacy header a guide section's responses used to be reported under, or '' when the section
 * matches none. Only used to recognize a note produced by the old prompt (see
 * `LEGACY_NOTE_SECTIONS`); it never decides how the note is written.
 */
export const legacyNoteSection = (sectionId: string, sectionLabel: string): string => {
  const haystack = `${sectionId} ${sectionLabel}`.toLowerCase();
  return LEGACY_SECTION_RULES.find(({ pattern }) => pattern.test(haystack))?.section ?? '';
};

/**
 * No guide section label comes close; anything longer is malformed input, not a header. Exported so
 * every place that runs a trailing-punctuation regex over a candidate header clips identically — a
 * line longer than this cannot match a registered key anyway, since registration clips here too.
 */
export const MAX_HEADER_LENGTH = 200;

/**
 * Header text reduced to a comparable key, so a section is matched whatever casing, spacing,
 * emphasis or trailing punctuation the model echoes it with. Not a fuzzy match: the words still
 * have to be the same.
 *
 * Emphasis characters are dropped because the model drifts between "### Blood Sugar Review",
 * "### **Blood Sugar Review**" and "**Blood Sugar Review**:" for the same section, and a header
 * that fails to match does not just lose its own line — it sends the whole section body somewhere
 * else. Both sides of the comparison run through here, so a label authored with an asterisk is
 * normalized identically when it is registered.
 */
export const normalizeNoteHeader = (value: string): string =>
  // Clipped first: section headers are short, and `[:.#\s]+$` backtracks quadratically, so an
  // unbounded punctuation run out of the model or a malformed Firestore label would block the UI.
  String(value ?? '')
    .slice(0, MAX_HEADER_LENGTH)
    .toLowerCase()
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/[:.#\s]+$/, '')
    .trim();
